// CapFooter — figure S4's map footer, the one line under the atlas that says
// what is and is not on it:
//
//   Joint projection anchored on corpus landmarks · holes / bridges / frontier
//   edges validated in full space (8 candidates, 0 discarded as a 2-D artifact)
//   · showing every document for 5 inventions and 30 of 62 for Aurora Battery
//   Cathode · computed over everything you're entitled to see · scope: your
//   inventions · 1 archived invention isn't mapped — restore it to include it
//
// The point of this component is that a PARTIAL view must SAY it is partial. A
// map that silently drops a document, an invention or a whole member project
// reads exactly like a complete one, and the reader has no way to tell the
// difference. So every cap governing the own-scope atlas gets a clause whether
// or not it bit, in plain words, naming the invention when it did — and the
// word "sampled" appears ONLY when a sample genuinely happened.
//
// The three real caps, read from the deployed backend, not invented here:
//   - MAX_DOCS_PER_MEMBER_FOR_PORTFOLIO = 30
//     (whitespace/portfolio_projection.py) — per invention, applied BEFORE
//     the global vector cap.
//   - MAX_PROJECTION_VECTORS = 200 (the same module's global cap, applied
//     across all inventions after the per-invention one).
//   - MAX_MEMBER_PROJECTS_FOR_MATRIX = 8
//     (whitespace/portfolio_synergy.py) — the synergy matrix drops members
//     past 8 and returns them in truncated_member_project_ids. bob owns 9
//     inventions, so this one genuinely bites.
//
// NEVER INVENT A DENOMINATOR. The caller passes the real per-invention
// document_count from GET /projects and the real per-invention point count
// present in the portfolio_map layout.
import { OWN_SCOPE_ENTITLEMENT_LINE } from './ownScopeViews'

export const MAX_DOCS_PER_INVENTION = 30
export const MAX_PROJECTION_VECTORS = 200
export const MAX_MEMBERS_FOR_SYNERGY = 8

export interface CapFooterInvention {
  project_id: string
  name: string
  /** The real total from GET /projects. */
  document_count: number
  /** How many of this invention's documents actually have a point in the
   * own-scope portfolio_map layout. */
  mapped_point_count: number
}

export interface CapFooterProps {
  inventions: CapFooterInvention[]
  /** portfolio_synergy's truncated_member_project_ids. `undefined` means the
   * synergy matrix has not been served — which is NOT "nothing was dropped". */
  synergyTruncatedProjectIds?: string[]
  /** portfolio_topology's own counters, so the validation clause quotes the
   * real run rather than a shape read off the picture. */
  candidateCount?: number
  discardedCandidateCount?: number
  /** Archived inventions the map cannot draw, with a real way back. */
  archived?: { project_id: string; name: string }[]
  /** PUT /projects/{id} {status:'active'} — a real restore, not a link to
   * nowhere. Absent while the caller has no way to perform it. */
  onRestoreArchived?: (projectId: string) => void
  /** True once every sharing read has answered AND found nothing: no invention
   * of yours is shared into a portfolio, and nothing has been shared in to you
   * at a disclosure rung. The figure carries a "Shared in" chip; when the real
   * account has no such share, the absence is STATED here rather than left as a
   * blank the reader cannot interpret (review round 2, item 4). */
  noSharedInSources?: boolean
}

export default function CapFooter({
  inventions,
  synergyTruncatedProjectIds,
  candidateCount,
  discardedCandidateCount,
  archived = [],
  onRestoreArchived,
  noSharedInSources = false,
}: CapFooterProps) {
  const sampledDown = inventions.filter(
    (i) => i.document_count > MAX_DOCS_PER_INVENTION && i.mapped_point_count < i.document_count,
  )
  // Documents that have NO point although no cap bit: a point exists only once
  // a document's embedding has completed, so a failed or pending embedding
  // leaves the document off the map. Computed from the two real numbers this
  // component already holds, never guessed.
  const shortfall = inventions
    .filter((i) => i.document_count <= MAX_DOCS_PER_INVENTION && i.mapped_point_count < i.document_count)
    .map((i) => ({ ...i, missing: i.document_count - i.mapped_point_count }))
  const fullyDrawn = inventions.filter((i) => i.mapped_point_count >= i.document_count && i.document_count > 0)
  const totalDocuments = inventions.reduce((n, i) => n + i.document_count, 0)
  const missingPoints = shortfall.reduce((n, i) => n + i.missing, 0)
  const totalMappedPoints = inventions.reduce((n, i) => n + i.mapped_point_count, 0)
  const vectorCapBit = totalMappedPoints >= MAX_PROJECTION_VECTORS

  const byId = new Map(inventions.map((i) => [i.project_id, i]))
  // Story #6762: an id that is not one of the reader's own inventions (a
  // project shared in to them) has no name here. Printing the raw id told the
  // reader nothing, so it is counted instead of named.
  const truncatedIds = synergyTruncatedProjectIds ?? []
  const truncated = truncatedIds.flatMap((id) => {
    const inv = byId.get(id)
    return inv ? [inv] : []
  })
  const unnamedLeftOut = truncatedIds.length - truncated.length
  const leftOutList = [
    ...truncated.map((i) => i.name),
    ...(unnamedLeftOut > 0
      ? [`${unnamedLeftOut} shared-in invention${unnamedLeftOut === 1 ? '' : 's'} not listed by name here`]
      : []),
  ].join(', ')
  const truncatedOnMap = truncated.filter((i) => i.mapped_point_count > 0).map((i) => i.name)
  const truncatedOffMap = truncated.filter((i) => i.mapped_point_count === 0).map((i) => i.name)

  const validation =
    candidateCount === undefined
      ? 'holes, bridges and open ground checked beyond the 2-D picture — the run’s candidate counts were not served'
      : `holes, bridges and open ground checked beyond the 2-D picture (${candidateCount} candidate${candidateCount === 1 ? '' : 's'} checked, ${discardedCandidateCount ?? 0} left off the map)`

  const coverage = sampledDown.length
    ? `${fullyDrawn.length > 0 ? `showing every document for ${fullyDrawn.length} invention${fullyDrawn.length === 1 ? '' : 's'} and ` : 'showing '}${sampledDown
        .map((i) => `${i.mapped_point_count} of ${i.document_count} for ${i.name}`)
        .join(', ')} — the map draws at most ${MAX_DOCS_PER_INVENTION} documents per invention`
    : missingPoints > 0
      ? `no invention was cut back, but ${missingPoints} of ${totalDocuments} document${missingPoints === 1 ? '' : 's'} ${missingPoints === 1 ? 'has' : 'have'} no point yet (${shortfall
          .map((i) => `${i.name}: ${i.mapped_point_count} of ${i.document_count}`)
          .join('; ')}) — a document is drawn once its embedding completes`
      : `showing every document for ${fullyDrawn.length} invention${fullyDrawn.length === 1 ? '' : 's'}, none left out`

  const vectors = vectorCapBit
    ? `the map is full at ${MAX_PROJECTION_VECTORS} points: ${totalMappedPoints} drawn, anything beyond was not placed`
    : `${totalMappedPoints} of the ${MAX_PROJECTION_VECTORS} points the map can hold are used, so nothing was dropped`

  const synergyClause =
    synergyTruncatedProjectIds === undefined
      ? `the synergy comparison has not run, so nothing was left out of it and nothing counted either`
      : truncatedIds.length > 0
        ? `the synergy comparison handles ${MAX_MEMBERS_FOR_SYNERGY} inventions at a time, so ${truncatedIds.length === 1 ? 'this one is' : 'these are'} left out of it: ${leftOutList}${
            truncated.length === 0
              ? ''
              : truncatedOnMap.length > 0 && truncatedOffMap.length === 0
                ? ' (still on the map above)'
                : truncatedOnMap.length === 0
                  ? ' (not on the map above either — no documents yet)'
                  : ` (${truncatedOnMap.join(', ')} still on the map above; ${truncatedOffMap.join(', ')} not drawn there either — no documents yet)`
          }`
        : `every invention is in the synergy comparison`

  return (
    <div data-part="CapFooter" className="mt-2 text-[11px] leading-relaxed text-gray-500">
      <p>
        Positions are measured against reference patents and papers · {validation} ·{' '}
        <b className="font-semibold text-iflow-800">{coverage}</b> · {vectors} · {synergyClause} ·{' '}
        <b className="font-semibold text-iflow-800">{OWN_SCOPE_ENTITLEMENT_LINE}</b>
        {archived.length > 0 && (
          <>
            {' · '}
            {archived.length} archived invention{archived.length === 1 ? " isn't" : "s aren't"} mapped
            {onRestoreArchived && (
              <>
                {' — '}
                {archived.map((a, i) => (
                  <span key={a.project_id}>
                    {i > 0 && ', '}
                    <button
                      type="button"
                      onClick={() => onRestoreArchived(a.project_id)}
                      className="font-semibold text-iflow-600 underline hover:text-iflow-800"
                    >
                      restore {a.name} to include it
                    </button>
                  </span>
                ))}
              </>
            )}
          </>
        )}
      </p>
      {noSharedInSources && (
        <p className="mt-1">
          No &quot;shared in&quot; row is drawn beside your roster: none of your inventions is
          shared into a portfolio, and nothing has been shared in to you at a disclosure rung. That
          is what your sharing actually says today, not a part of the screen that is missing.
        </p>
      )}
      <p className="mt-1 italic">
        Scores compare against the patents and papers loaded so far — coverage grows as more are
        added.
      </p>
    </div>
  )
}
